import { DataCatalogApolloClient } from '@tableau/catalog-ui-temp';
import {
  GraphQLSliceable,
  GraphQLSliceableParams,
  PaginationQueryVariables,
  PartialQueryVariables,
  SliceResult,
} from '@tableau/catalog-ui-temp';

import { ISliceable } from '../../httpClient/fetchers/ISliceable';
import { ListResult } from '../../httpClient/fetchers/ListResult';
import { reportGraphQLError } from '../CatalogErrorReporterService';


export interface ItemWithId {
  id: string;
}

export type GraphQLSliceableFactory<TQueryVariables extends PaginationQueryVariables, TResultItem extends ItemWithId> =
  (partialQueryVariables: PartialQueryVariables<TQueryVariables>) => ISliceable<TResultItem>;

export function makeSliceableFactoryForGraphQLQuery<
    TQueryResultListNode extends ItemWithId,
    TResultItem extends ItemWithId,
    TQueryVariables extends PaginationQueryVariables,
    TQueryResults,
>(
  params: GraphQLSliceableParams<TQueryResults, TQueryResultListNode, TResultItem>,
  client: DataCatalogApolloClient,
): GraphQLSliceableFactory<TQueryVariables, TResultItem> {

  return function graphQLSliceableFactory(partialQueryVariables: PartialQueryVariables<TQueryVariables>): ISliceable<TResultItem> {

    const sliceable = new GraphQLSliceable<TQueryResultListNode, TResultItem, TQueryVariables, TQueryResults>(
      params, partialQueryVariables, client
    );

    async function getSliceAsListResult({ start, size }: { start: number, size: number }) {
      const slice = await sliceable.getSlice({ start, size });
      reportErrorsFromSliceResult(slice);
      return new ListResult(slice.items as TResultItem[], start, slice.totalCount);
    }

    return {
      getSlice: (start: number, size: number) => getSliceAsListResult({ start, size }),
    };
  };
}

/**
 * Reports the graphql errors back into the Data Catalog code, where they're rendered
 */
function reportErrorsFromSliceResult(sliceResult: SliceResult<ItemWithId>) {
  if (sliceResult.errors) {
    for (const error of sliceResult.errors) {
      reportGraphQLError(error);
    }
  }
}
